(function (global) {
/* global runtime, React */
// Prosjekt- og timeregistreringsadaptere. Lastes før backend.jsx slik at
// fasaden kan eksponere de samme globale funksjonsnavnene uten at domenet
// ligger i den monolittiske backendfila.
// ------------------------------------------------------------- prosjekter
// Prosjekter, aktiviteter og timeføring. Regnereglene ligger databasefritt i
// `src/services/projectTimeTracking.js`; her er lagring, tilgangsstyring og
// koblingen mot fakturamotoren. Timene lever i samme state-dokument som resten
// av regnskapet, saa de arver tilgangsreglene for foretaket.
const projectTimeApi = () => globalThis.PlummeProjectTimeTracking;

const emitProjectsChanged = () => {
  window.dispatchEvent(new Event(BACKEND_EVENTS.projects));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
};

const getProjectTimeModel = () =>
  cachedDerivedModel('projectTimeTracking', () => projectTimeApi().buildModel(mergeState(runtime.appState)));

// Prosjektet peker paa kunden med id. Navnet lagres i tillegg, slik at et
// prosjekt beholder en lesbar kunde selv om kunden senere arkiveres.
const resolveProjectCustomer = (state, project) => {
  const customers = safeArray(state.customers);
  const customerId = String(project?.customerId || '').trim();
  if (customerId) return customers.find((customer) => customer.id === customerId) || null;
  const customerName = String(project?.customerName || '').trim().toLowerCase();
  if (!customerName) return null;
  return customers.find((customer) => String(customer.name || '').trim().toLowerCase() === customerName) || null;
};

const saveProjectRecord = async (uiProject) => {
  const api = projectTimeApi();
  let savedProject = null;
  await updateStateAndPersist((state) => {
    const projects = safeArray(state.projects);
    const existing = projects.find((project) => project.id === uiProject?.id) || {};
    const customerId = String(uiProject?.customerId ?? existing.customerId ?? '').trim();
    const customer = customerId ? safeArray(state.customers).find((entry) => entry.id === customerId) : null;
    if (customerId && !customer) throw new Error(t('svc.customerNotFound'));
    const requestedNumber = Math.round(num(uiProject?.projectNumber));
    const project = api.normalizeProject({
      ...uiProject,
      customerId: customer ? customer.id : '',
      customerName: customer ? customer.name : String(uiProject?.customerName ?? existing.customerName ?? '').trim(),
      projectNumber: requestedNumber || existing.projectNumber || api.nextProjectNumber(projects),
    }, existing);
    // Prosjektnummeret er hvordan brukeren refererer til prosjektet i dialog med
    // kunden. To prosjekter med samme nummer gjoer den referansen verdilos.
    const numberTaken = projects.some((entry) => entry.id !== project.id && Math.round(num(entry.projectNumber)) === project.projectNumber);
    if (numberTaken) throw new Error(t('svc.projectNumberTaken'));
    savedProject = project;
    return { ...state, projects: [...projects.filter((entry) => entry.id !== project.id), project] };
  });
  emitProjectsChanged();
  return savedProject;
};

const deleteProjectRecord = async (projectId) => {
  if (!projectId) throw new Error(t('svc.projectNotFound'));
  await updateStateAndPersist((state) => {
    const projects = safeArray(state.projects);
    if (!projects.some((project) => project.id === projectId)) throw new Error(t('svc.projectNotFound'));
    // Registrerte timer er regnskapsgrunnlag. Et prosjekt med timer skal
    // avsluttes, ikke slettes, ellers mister allerede fakturerte timer sporet
    // tilbake til hva de gjaldt.
    if (safeArray(state.timeEntries).some((entry) => entry.projectId === projectId)) {
      throw new Error(t('svc.projectHasHours'));
    }
    return {
      ...state,
      projects: projects.filter((project) => project.id !== projectId),
      projectActivities: safeArray(state.projectActivities).filter((activity) => activity.projectId !== projectId),
      timeTrackingSettings: {
        ...projectTimeApi().normalizeSettings(state.timeTrackingSettings),
        pinnedRows: projectTimeApi().normalizeSettings(state.timeTrackingSettings).pinnedRows.filter((row) => row.projectId !== projectId),
      },
    };
  });
  emitProjectsChanged();
};

const saveProjectActivity = async (uiActivity) => {
  const api = projectTimeApi();
  let savedActivity = null;
  await updateStateAndPersist((state) => {
    const activities = safeArray(state.projectActivities);
    const existing = activities.find((activity) => activity.id === uiActivity?.id) || {};
    const projectId = String(uiActivity?.projectId ?? existing.projectId ?? '').trim();
    if (!safeArray(state.projects).some((project) => project.id === projectId)) throw new Error(t('svc.projectNotFound'));
    const productId = String(uiActivity?.productId ?? existing.productId ?? '').trim();
    if (productId && !safeArray(state.products).some((product) => product.id === productId)) throw new Error(t('svc.productNotFound'));
    const activity = api.normalizeActivity({ ...uiActivity, projectId, productId }, existing);
    savedActivity = activity;
    return { ...state, projectActivities: [...activities.filter((entry) => entry.id !== activity.id), activity] };
  });
  emitProjectsChanged();
  return savedActivity;
};

const deleteProjectActivity = async (activityId) => {
  if (!activityId) throw new Error(t('svc.activityNotFound'));
  await updateStateAndPersist((state) => {
    const activities = safeArray(state.projectActivities);
    if (!activities.some((activity) => activity.id === activityId)) throw new Error(t('svc.activityNotFound'));
    if (safeArray(state.timeEntries).some((entry) => entry.activityId === activityId)) {
      throw new Error(t('svc.activityHasHours'));
    }
    return {
      ...state,
      projectActivities: activities.filter((activity) => activity.id !== activityId),
      timeTrackingSettings: {
        ...projectTimeApi().normalizeSettings(state.timeTrackingSettings),
        pinnedRows: projectTimeApi().normalizeSettings(state.timeTrackingSettings).pinnedRows.filter((row) => row.activityId !== activityId),
      },
    };
  });
  emitProjectsChanged();
};

const saveTimeEntry = async (uiEntry) => {
  const api = projectTimeApi();
  let savedEntry = null;
  await updateStateAndPersist((state) => {
    const entries = safeArray(state.timeEntries);
    const existing = entries.find((entry) => entry.id === uiEntry?.id) || null;
    const settings = api.normalizeSettings(state.timeTrackingSettings);
    if (existing) api.validateEntryEditable({ settings }, api.normalizeTimeEntry(existing, existing));
    const activityId = String(uiEntry?.activityId ?? existing?.activityId ?? '').trim();
    const activity = safeArray(state.projectActivities).find((entry) => entry.id === activityId);
    if (!activity) throw new Error(t('svc.activityNotFound'));
    const project = safeArray(state.projects).find((entry) => entry.id === activity.projectId);
    if (!project) throw new Error(t('svc.projectNotFound'));
    // Aktiviteten eier prosjekttilhorigheten. Uten dette kunne UI-et sende en
    // aktivitet fra ett prosjekt sammen med et annet prosjekt, og timene ville
    // blitt fakturert til feil kunde.
    if (!existing && project.status === 'closed') throw new Error(t('svc.projectClosed'));
    const entry = api.normalizeTimeEntry({ ...uiEntry, projectId: project.id, activityId: activity.id }, existing || {});
    if (api.isDateLocked(settings, entry.date)) throw new Error(t('svc.timeTrackingLocked'));
    savedEntry = entry;
    return { ...state, timeEntries: [...entries.filter((item) => item.id !== entry.id), entry] };
  });
  emitProjectsChanged();
  return savedEntry;
};

const deleteTimeEntry = async (entryId) => {
  const api = projectTimeApi();
  if (!entryId) throw new Error(t('svc.timeEntryNotFound'));
  await updateStateAndPersist((state) => {
    const entries = safeArray(state.timeEntries);
    const existing = entries.find((entry) => entry.id === entryId);
    if (!existing) throw new Error(t('svc.timeEntryNotFound'));
    api.validateEntryEditable(
      { settings: api.normalizeSettings(state.timeTrackingSettings) },
      api.normalizeTimeEntry(existing, existing),
    );
    return { ...state, timeEntries: entries.filter((entry) => entry.id !== entryId) };
  });
  emitProjectsChanged();
};

const saveTimeTrackingSettings = async (patch = {}) => {
  const api = projectTimeApi();
  let savedSettings = null;
  await updateStateAndPersist((state) => {
    const current = api.normalizeSettings(state.timeTrackingSettings);
    const next = api.normalizeSettings({ ...current, ...patch });
    // Festede rader skal peke paa noe som finnes. Ellers blir uketabellen
    // staaende med tomme linjer etter at et prosjekt er avsluttet og slettet.
    const projectIds = new Set(safeArray(state.projects).map((project) => project.id));
    const activityIds = new Set(safeArray(state.projectActivities).map((activity) => activity.id));
    savedSettings = {
      ...next,
      pinnedRows: next.pinnedRows.filter((row) => projectIds.has(row.projectId) && activityIds.has(row.activityId)),
    };
    return { ...state, timeTrackingSettings: savedSettings };
  });
  emitProjectsChanged();
  return savedSettings;
};

/**
 * Fakturautkast fra ufakturerte timer.
 *
 * Timene grupperes per aktivitet til fakturalinjer i domenelaget, utkastet
 * lagres gjennom den vanlige fakturamotoren (`upsertInvoiceHttp`) slik at
 * nummerserie, mva og bokforing folger samme vei som ethvert annet salg, og
 * timene merkes som fakturert forst naar utkastet faktisk er lagret.
 */
const projectCostApi = () => window.PlummeProjectCostLink;

const buildTimeEntryInvoiceDraft = ({ projectId, entryIds = [], costKeys = [], includeTimesheet = true } = {}) => {
  const api = projectTimeApi();
  const state = mergeState(runtime.appState);
  const model = api.buildModel(state);
  const project = model.projectsById.get(String(projectId || ''));
  if (!project) throw new Error(t('svc.projectNotFound'));
  const customer = resolveProjectCustomer(state, project);
  if (!customer) throw new Error(t('svc.projectNeedsCustomer'));
  const selected = new Set(safeArray(entryIds).map((id) => String(id)));
  const entries = model.entries
    .filter((entry) => entry.projectId === project.id && entry.invoiceStatus !== 'invoiced' && selected.has(entry.id))
    .map((entry) => api.decorateEntry(entry, model))
    .filter((entry) => entry.activity && entry.activity.billing !== 'never');
  // Kostnadene som er valgt til viderefakturering. De kan sta alene: et
  // prosjekt kan ha utlegg a fakturere uten at det ble fort timer.
  const chosenCosts = new Set(safeArray(costKeys).map((key) => String(key)));
  const costs = chosenCosts.size
    ? projectCostApi().collectProjectCosts(state, { projectId: project.id }).filter((cost) => chosenCosts.has(cost.key))
    : [];
  if (!entries.length && !costs.length) throw new Error(t('svc.pickTimeEntries'));

  const products = safeArray(state.products).map(mapProductToUi);
  const lines = [...api.buildInvoiceLines(entries, { products }), ...projectCostApi().buildProjectCostInvoiceLines(costs)];
  if (!lines.length) throw new Error(t('svc.pickTimeEntries'));
  const timesheetRows = includeTimesheet ? api.buildTimesheetRows(entries) : [];
  const allDates = [...entries.map((entry) => entry.date), ...costs.map((cost) => cost.date)].filter(Boolean).sort();
  const periodFrom = allDates[0];
  const periodTo = allDates[allDates.length - 1];

  const invoice = buildInvoiceFromEditor({
    docType: 'faktura',
    customer: mapCustomerToUi(customer, state),
    invDate: todayIso(),
    reference: project.name,
    theirRef: project.contactPerson || '',
    descr: t('svc.timesheetInvoiceDescription', { project: project.name, from: periodFrom, to: periodTo }),
    lines: lines.map((line) => ({
      id: createId('line'),
      desc: line.description,
      note: line.note || undefined,
      productId: line.productId || undefined,
      productName: line.productName || undefined,
      itemNumber: line.itemNumber || undefined,
      unit: line.unit,
      qty: line.quantity,
      price: line.unitPrice,
      vat: line.vat,
      salesVatCode: line.salesVatCode,
      salesAccountCode: line.salesAccountCode,
      type: line.type,
    })),
  }, 'draft');

  return {
    invoice: stripUndefinedDeep({
      ...invoice,
      customerId: customer.id,
      serviceDate: periodFrom,
      serviceEndDate: periodTo,
      sourceProject: {
        projectId: project.id,
        projectNumber: project.projectNumber,
        projectName: project.name,
        timeEntryIds: entries.map((entry) => entry.id),
        costKeys: costs.map((cost) => cost.key),
        periodFrom,
        periodTo,
        hours: api.totalHours(entries),
      },
      timesheetRows: timesheetRows.length ? timesheetRows : undefined,
    }),
    entries,
    costs,
    lines,
    timesheetRows,
    hours: api.totalHours(entries),
    costAmount: projectCostApi().totalCostAmount(costs),
  };
};

const createInvoiceDraftFromTimeEntries = async (options = {}) => {
  const draft = buildTimeEntryInvoiceDraft(options);
  const { entries, costs, lines } = draft;
  const savedInvoice = await upsertInvoice(draft.invoice);

  const invoicedAt = nowIso();
  const invoicedIds = new Set(entries.map((entry) => entry.id));
  await updateStateAndPersist((current) => projectCostApi().applyProjectCostInvoiceState({
    ...current,
    timeEntries: safeArray(current.timeEntries).map((entry) => invoicedIds.has(entry.id)
      ? { ...entry, invoiceStatus: 'invoiced', invoiceId: savedInvoice.id, invoicedAt, updatedAt: invoicedAt }
      : entry),
  }, costs, { invoiceId: savedInvoice.id, invoicedAt }));
  emitProjectsChanged();

  return {
    invoice: mapInvoiceToUi(savedInvoice, mergeState(runtime.appState)),
    hours: draft.hours,
    lineCount: lines.length,
    entryCount: entries.length,
    costCount: costs.length,
  };
};

/**
 * Angrer faktureringen av timer naar fakturautkastet slettes eller krediteres.
 * Timene blir ufakturerte igjen og dukker opp i fakturagrunnlaget, slik at de
 * ikke blir borte fordi et utkast ble forkastet.
 */
const releaseTimeEntriesFromInvoice = async (invoiceId) => {
  const targetId = String(invoiceId || '');
  if (!targetId) return 0;
  let released = 0;
  await updateStateAndPersist((state) => {
    const entries = safeArray(state.timeEntries);
    const next = entries.map((entry) => {
      if (entry.invoiceId !== targetId) return entry;
      released += 1;
      return { ...entry, invoiceStatus: 'open', invoiceId: '', invoicedAt: '', updatedAt: nowIso() };
    });
    // Kostnadene ma frigis sammen med timene. Blir de staaende «fakturert» etter
    // at utkastet er slettet, forsvinner de fra grunnlaget for godt.
    const costs = projectCostApi().releaseProjectCostsFromInvoice({ ...state, timeEntries: next }, targetId);
    released += costs.released;
    return released ? costs.state : state;
  });
  if (released) emitProjectsChanged();
  return released;
};

global.PlummeBackendProjects = {
  projectTimeApi,
  emitProjectsChanged,
  getProjectTimeModel,
  resolveProjectCustomer,
  saveProjectRecord,
  deleteProjectRecord,
  saveProjectActivity,
  deleteProjectActivity,
  saveTimeEntry,
  deleteTimeEntry,
  saveTimeTrackingSettings,
  buildTimeEntryInvoiceDraft,
  createInvoiceDraftFromTimeEntries,
  releaseTimeEntriesFromInvoice,
};
})(typeof window !== 'undefined' ? window : globalThis);
